// fsiExport.js — Excel sheet for the free swell index, same layout as the lab's "FSI" sheet. (The template has no graph.)
const THIN = { style: 'thin', color: { argb: 'FF000000' } };
const BOX = { top: THIN, left: THIN, bottom: THIN, right: THIN };
const font = (size = 10, bold = false) => ({ name: 'Arial', size, bold });
const utcDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

export function addFSISheet(wb, { header, inputs, result }) {
  const ws = wb.addWorksheet('FSI', {
    pageSetup: { paperSize: 9, orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 1 },
  });
  [19, 30.6, 21.4, 21.4, 19].forEach((w, i) => { ws.getColumn(i + 1).width = w; });
  const center = { horizontal: 'center', vertical: 'middle', wrapText: true };
  const left = { horizontal: 'left', vertical: 'middle', wrapText: true };
  const area = (r1, c1, r2, c2, value, o = {}) => {
    for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) {
      const cell = ws.getCell(r, c);
      cell.border = o.border ?? BOX; cell.font = o.font ?? font(); cell.alignment = o.align ?? center;
    }
    const top = ws.getCell(r1, c1);
    if (value !== undefined && value !== null && value !== '') top.value = value;
    if (o.numFmt) top.numFmt = o.numFmt;
    if (r2 > r1 || c2 > c1) ws.mergeCells(r1, c1, r2, c2);
  };
  [32.2, 27, 20.4, 24, 19.9, 24.9, 28.1, 28.9, 31.9, 28.9, 28.9, 31.9, 51.6, 63, 63, 46.9, 15, 15].forEach((h, i) => { ws.getRow(i + 1).height = h; });

  area(1, 1, 1, 1, 'Name of Project', { font: font(11, true), align: left });
  area(1, 2, 1, 5, header.project, { font: font(11), align: left });
  area(2, 1, 2, 1, 'Issue to ', { font: font(11, true), align: left });
  area(2, 2, 2, 5, header.issueTo, { font: font(11), align: left });
  area(3, 1, 3, 5, 'FREE SWELL INDEX OF SOILS', { font: font(12, true) });
  area(4, 1, 4, 5, 'As per IS: 2720(Part – 40) – 1977 ', { font: font(10, true) });

  // same spacing as the lab sheet: details rows with empty rows between them
  area(5, 1, 5, 5, '', { border: {} });
  const info = (r, l1, v1, l2, v2, isDate) => {
    area(r, 1, r, 1, l1, { align: left }); area(r, 2, r, 3, v1, { align: left });
    area(r, 4, r, 4, l2, { align: left }); area(r, 5, r, 5, v2, isDate ? { numFmt: 'dd/mm/yyyy' } : {});
  };
  area(6, 1, 6, 5, '', {});
  info(7, 'Location/Source:', header.location, 'Date of Receiving', utcDate(header.receivedOn), true);
  area(8, 1, 8, 5, '', {});
  info(9, 'Type of Materials:', header.material, 'Date of Testing', utcDate(header.testedOn), true);
  area(10, 1, 11, 5, '', {});
  info(12, 'Sample No.', header.sampleNo, 'Tested by', header.testedBy);

  // table
  ['S.No', 'Description', 'Sample No:\n I', 'Sample No:\n II', 'Average'].forEach((t, i) => area(13, i + 1, 13, i + 1, t));
  const [s1, s2] = inputs.specimens;
  area(14, 1, 14, 1, 1);
  area(14, 2, 14, 2, 'Volume of the soil specimen read from the graduated cylinder containing with water (Vd) in cc', { align: left });
  area(14, 3, 14, 3, s1.vdCc, { numFmt: '0.00' });
  area(14, 4, 14, 4, s2.vdCc, { numFmt: '0.00' });
  area(15, 1, 15, 1, 2);
  area(15, 2, 15, 2, 'Volume of the soil specimen read from the graduated cylinder containing with kerosene (Vk) in cc', { align: left });
  area(15, 3, 15, 3, s1.vkCc, { numFmt: '0.00' });
  area(15, 4, 15, 4, s2.vkCc, { numFmt: '0.00' });
  area(14, 5, 15, 5, '');
  area(16, 1, 16, 1, '');
  area(16, 2, 16, 2, 'F.S.I % = (Vd -  Vk)/ Vk * 100', { font: font(10, true), align: left });
  area(16, 3, 16, 3, { formula: 'ROUND((C14-C15)/C15*100,2)', result: Number(result.values[0].toFixed(2)) }, { numFmt: '0.00' });
  area(16, 4, 16, 4, { formula: 'ROUND((D14-D15)/D15*100,2)', result: Number(result.values[1].toFixed(2)) }, { numFmt: '0.00' });
  area(16, 5, 16, 5, { formula: 'AVERAGE(C16:D16)', result: (Number(result.values[0].toFixed(2)) + Number(result.values[1].toFixed(2))) / 2 },
    { font: font(10, true), numFmt: '0.0' });

  area(18, 1, 18, 5, `Settling time: ${inputs.settleHours} h   |   Checked by: ${header.checkedBy}`, { font: font(8), align: left, border: {} });
  area(26, 1, 29, 5, 'Authorised signatory', { font: font(14, true), align: { horizontal: 'right', vertical: 'middle' }, border: {} });
  return ws;
}

export const fsiExporter = {
  async addSheet(wb, { header, data }) {
    addFSISheet(wb, { header, inputs: data.inputs, result: data.result });
    return []; // no graph in the FSI template
  },
};
